import { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';

import Typography from '@mui/material/Typography';
import { FORM_ERROR } from 'final-form';

import { breadcrumbsActions } from '@store/breadcrumbs';
import { usePermissions } from '@store/auth';
import { showSuccessMessage } from '@store/dashboard';
import ApplicationPageTemplate from '@dashboardComponents/ApplicationPageTemplate/ApplicationPageTemplate';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@components/FormAccordion';
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';
import { formsActions } from '@store/forms';
import {
  addLOB,
  editLOB,
  LOB_LIST_ROUTE,
} from '../../payment-options.breadcrumbs';

import {
  getPlatforms,
  selectPlatforms,
} from '../../store/payment-options-platforms';
import { getCurrencies } from '../../store/payment-options-currencies';
import {
  addNewMerchant,
  getLOBDetails,
  getMerchantTabPriority,
  paymentOptionsMerchantsActions,
  updateMerchantTabPriority,
  updateMerchant,
  selectSelectedMerchant,
} from '../../store/payment-options-merchants';

import LOBDetailsForm from './LOBDetailsForm';
import LOBChannelsForm from './LOBChannelsForm';
import LOBPaymentMethods from './LOBPaymentMethods';
import LOBContextualMessages from './LOBContextualMessages';
import ProcessingFees from './ProcessingFees';

import {
  LOB_CHANNELS_FORM_NAME,
  LOB_DETAILS_FORM_NAME,
  LOB_PROPERTIES_FORM_NAME,
  MERCHANT_TABS_ORDER_FORM_NAME,
  PROPERTIES_LIST,
} from '../../payment-options.constants';

import FormSaveAndSubmit from '../../../../components/FormSaveAndSubmit';
import { selectTabList } from '../../store/payment-options-tabs';

export default function MerchantForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const tabs = useSelector(selectTabList);
  const platforms = useSelector(selectPlatforms);
  const selectedMerchant = useSelector(selectSelectedMerchant);

  const lobDetailsForm = useSelector(
    (state) => state.forms[LOB_DETAILS_FORM_NAME],
  );
  const lobPropertiesForm = useSelector(
    (state) => state.forms[LOB_PROPERTIES_FORM_NAME],
  );
  const lobChannelsForm = useSelector(
    (state) => state.forms[LOB_CHANNELS_FORM_NAME],
  );
  const tabsOrderForm = useSelector(
    (state) => state.forms[MERCHANT_TABS_ORDER_FORM_NAME],
  );

  const { id } = useParams();

  const permissions = usePermissions();
  const canEdit = permissions?.canEditPaymentOptions;

  // A view-only user can still open an existing LOB, but must not reach the
  // create form directly via its URL.
  useEffect(() => {
    if (id === undefined && !permissions?.canEditPaymentOptions) {
      navigate(LOB_LIST_ROUTE);
    }
  }, [id, navigate, permissions]);

  const [isFetchingData, setIsFetchingData] = useState(true);
  const [expanded, setExpanded] = useState('panel3');

  const [formValue, setFormValue] = useState({});

  const detailsFormRef = useRef();
  const [detailsFormError, setDetailsFormError] = useState('');

  const channelsFormRef = useRef();
  const [channelsFormError, setChannelsFormError] = useState('');

  const [tabOrderChanged, setTabOrderChanged] = useState(false);
  const [tabsFormSuccess, setTabsFormSuccess] = useState('');
  const [tabsFormError, setTabsFormError] = useState('');
  const [openDialog, setOpenDialog] = useState({ show: false });
  const [openDialog2, setOpenDialog2] = useState({ show: false });

  const convertToProperties = (arr) => {
    const properties = {};

    arr.forEach(({ key, value }) => {
      if (key === 'properties') return;

      let parsedValue;

      if (value === 'true') parsedValue = true;
      else if (value === 'false') parsedValue = false;
      else if (typeof value === 'string' && value.trim() !== '') {
        const num = Number(value);
        if (!Number.isNaN(num) && value.trim() !== '') parsedValue = num;
        else parsedValue = value;
      } else {
        parsedValue = value;
      }

      properties[key] = parsedValue;
    });

    return properties;
  };

  const getInitializedData = () => {
    let formData = {};

    if (id === undefined) {
      formData = {
        lobCode: '',
        lobName: '',
        properties: PROPERTIES_LIST.reduce(
          (prev, prop) => ({ ...prev, [prop.key]: prop.defaultValue }),
          {},
        ),
        channelHash: {},
        tabsOrder: {},
        platforms: platforms.map((platform) => ({
          channelHash: '',
          platformCode: platform.code,
          platformId: platform.id,
          platformName: platform.name,
          // @TODO: change this to DnD
          tabsOrder: JSON.stringify(
            tabs.map((tab) => tab.code),
            null,
            2,
          ),
        })),
      };
    } else {
      formData = {
        ...selectedMerchant,
        platforms: platforms.map((platform) => {
          const { merchantDetails } = selectedMerchant;

          const channel = merchantDetails
            ? merchantDetails.find(
                (detailPlatform) => platform.id === detailPlatform.platformId,
              )
            : null;

          // has channel hash
          if (channel) {
            return {
              ...channel,
              channelHash: channel.channelHash,
              // @TODO: change this to DnD
              tabsOrder: JSON.stringify(
                tabs.map((tab) => tab.code),
                null,
                2,
              ),
            };
          }

          // no channel hash
          return {
            channelHash: '',
            platformCode: platform.code,
            platformId: platform.id,
            platformName: platform.name,
            // @TODO: change this to DnD
            tabsOrder: JSON.stringify(
              tabs.map((tab) => tab.code),
              null,
              2,
            ),
          };
        }),
      };
    }

    return formData;
  };

  useEffect(() => {
    setIsFetchingData(true);

    if (id !== undefined) {
      dispatch(breadcrumbsActions.setBreadcrumbs(editLOB(id)));
      Promise.all([
        dispatch(getCurrencies()),
        dispatch(getPlatforms()),
        dispatch(getLOBDetails(id)),
      ]).then(() => setIsFetchingData(false));
    } else {
      dispatch(breadcrumbsActions.setBreadcrumbs(addLOB));
      Promise.all([
        dispatch(getCurrencies()),
        dispatch(getPlatforms()),
        dispatch(paymentOptionsMerchantsActions.resetSelectedMerchant()),
      ]).then(() => setIsFetchingData(false));
    }
  }, [id, dispatch]);

  useEffect(() => {
    const value = getInitializedData();

    setFormValue(value);
  }, [selectedMerchant, tabs, platforms, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePanel = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const handleDetailsFormReset = () => {
    detailsFormRef.current.reset();

    const value = getInitializedData();

    setFormValue(value);
    setDetailsFormError('');
  };

  /**
   * Handles create and update of LOB Details
   */
  const handleSubmit = () => {
    const { values } = lobDetailsForm;

    if (!values.lobCode) return { [FORM_ERROR]: 'Merchant Code is required' };
    if (!values.lobName) return { [FORM_ERROR]: 'Merchant Name is required' };

    const payload = {
      id,
      lobName: values.lobName,
      lobCode: values.lobCode,
    };

    if (lobPropertiesForm.values.properties.length > 0) {
      // Need to do this because API replaces the properties set in DB as a whole
      payload.properties = {
        // ...selectedMerchant.properties,
        ...convertToProperties(lobPropertiesForm.values.properties),
      };
    }

    if (values.channelHash) {
      payload.channelHash = {}; // @TODO implement this
    }

    const tabsOrder = {};
    if (values.tabOrder) {
      try {
        Object.keys(values.tabsOrder).forEach((k) => {
          const platformTabs = JSON.parse(values.tabsOrder[k]);

          if (!Array.isArray(platformTabs))
            throw new Error(`Tabs order for platform '${k}' is Not an Array`);

          tabs.forEach((t) => {
            if (values.tabsOrder[k].indexOf(t.code) === -1)
              throw new Error(
                `Tabs Order for platform '${k}' is missing '${t.code}' tab`,
              );
          });
          tabsOrder[k] = platformTabs;
        });

        payload.tabOrder = tabsOrder;
      } catch (error) {
        return {
          [FORM_ERROR]: `Tabs Order for platforms should be a valid Json Array and includes all tabs, ${error}`,
        };
      }
    }

    return dispatch(
      id !== undefined ? updateMerchant(payload) : addNewMerchant(payload),
    ).then((data) => {
      if (data.payload && data.payload.status === 'SUCCESS') {
        dispatch(
          showSuccessMessage(
            `Merchant ${id !== undefined ? 'updated' : 'added'} successfully`,
          ),
        );
        navigate(LOB_LIST_ROUTE);
      } else {
        setDetailsFormError(data.payload.message);
      }
    });
  };

  const handleChannelsReset = () => {
    channelsFormRef.current.reset();
    setChannelsFormError('');
  };

  /**
   * Used only in updating channel hashes for already saved LOBs.
   * For LOB creation, it's being handled by handleSubmit
   */
  const handleChannelsSubmit = () => {
    setChannelsFormError('');

    if (id === undefined) {
      const { values } = lobDetailsForm;
      if (!values.lobCode || !values.lobName) {
        setChannelsFormError(
          `Fill up the details form first. Required: ${
            !values.lobName ? 'LOB Name' : ''
          }${!values.lobName ? ', ' : ''} ${
            !values.lobCode && 'OMS Order Type'
          }`,
        );
        return false;
      }
    }

    const selectedMerchantChannels = selectedMerchant.merchantDetails
      ? selectedMerchant.merchantDetails.reduce(
          (prev, detail) => ({
            ...prev,
            [detail.platformCode]: detail.channelHash,
          }),
          {},
        )
      : [];
    const channelHash = lobChannelsForm.values.platforms.reduce(
      (prev, curr) => {
        if (!curr.isAdded) return prev;
        if (curr.channelHash === selectedMerchantChannels[curr.platformCode])
          return prev;

        return { ...prev, [curr.platformCode]: curr.channelHash || '' };
      },
      {},
    );

    const { lobName, lobCode } = lobDetailsForm.values;

    const payload = {
      id,
      lobName,
      lobCode,
      channelHash,
    };

    if (lobPropertiesForm.values.properties.length > 0) {
      // Need to do this because API replaces the properties set in DB as a whole
      payload.properties = {
        // ...selectedMerchant.properties,
        ...convertToProperties(lobPropertiesForm.values.properties),
      };
    }

    return dispatch(
      id !== undefined ? updateMerchant(payload) : addNewMerchant(payload),
    ).then((data) => {
      if (data.payload && data.payload.status === 'SUCCESS') {
        dispatch(
          showSuccessMessage(
            `Merchant ${id !== undefined ? 'updated' : 'added'} successfully`,
          ),
        );

        if (id === undefined) {
          navigate(LOB_LIST_ROUTE);
        }
      } else {
        setChannelsFormError(data.payload.message);
      }
    });
  };

  /**
   * Handles saving of merchant tab order
   */
  const handleTabOrderChange = useCallback(
    ({ isChanged, selectedPlatform, selectedCurrency, tabOrder }) => {
      setTabsFormError('');
      setTabsFormSuccess('');
      setTabOrderChanged(isChanged);
      dispatch(
        formsActions.setForm({
          name: MERCHANT_TABS_ORDER_FORM_NAME,
          state: {
            selectedPlatform,
            tabOrder,
            selectedCurrency,
          },
        }),
      );
    },
    [dispatch],
  );

  const handleTabOrderReset = () => {
    setTabOrderChanged(false);
  };

  /**
   * Handles saving of merchant tab order
   */
  const handleTabOrderSubmit = () => {
    if (!id) return false;

    const { merchantId } = tabsOrderForm.selectedPlatform;
    const tabsPriority = tabsOrderForm.tabOrder.reduce(
      (prev, tab, index) => ({ ...prev, [tab.id]: index + 1 }),
      {},
    );

    const payload = {
      merchantIds: [merchantId],
      tabsPriority,
    };

    return dispatch(updateMerchantTabPriority(payload)).then((data) => {
      if (data.payload && data.payload.status === 'SUCCESS') {
        dispatch(getMerchantTabPriority(selectedMerchant.lobId));
        dispatch(showSuccessMessage('Tab order updated successfully'));
        setTabsFormSuccess('Tab order updated successfully');
        setTabOrderChanged(false);
      } else {
        setTabsFormError(data.payload.message);
      }
    });
  };

  const handlePreSubmit = (e, name) => {
    e.preventDefault();
    if (name === 'lobProperties') setOpenDialog({ show: true });
    else setOpenDialog2({ show: true }); // open the confirmation dialog if prod
  };

  return (
    <ApplicationPageTemplate
      title={
        id !== undefined ? selectedMerchant.lobName : 'New Line of Business'
      }
      description={
        id !== undefined
          ? `Last Updated by ${selectedMerchant.modifiedBy} at ${moment(
              selectedMerchant.modifiedAt,
            ).format('YYYY/MM/DD - HH:mm')}`
          : ''
      }
      pageReady={!isFetchingData}>
      {!isFetchingData && (
        <div>
          <Accordion
            expanded={expanded === 'panel1'}
            onChange={togglePanel('panel1')}>
            <AccordionSummary
              aria-controls="panel1bh-content"
              id="panel1bh-header">
              {/* Header: Title */}
              <Typography
                sx={{ flex: 2, maxWidth: '33%', flexShrink: 1 }}
                variant="formHeader">
                LOB Properties
              </Typography>

              {/* Header: Subtitle / helper */}
              {expanded === 'panel1' ? null : (
                <Typography
                  sx={{ flex: 2, color: 'text.secondary', flexShrink: 1 }}>
                  Update name and properties
                </Typography>
              )}
              <ProdConfirmationDialog
                openDialog={openDialog}
                yesHandler={handleSubmit}
              />
              {/* Header: Actions when expanded */}
              {expanded === 'panel1' ? (
                <FormSaveAndSubmit
                  sx={{ flex: 10, color: 'text.secondary' }}
                  id={id}
                  resetDisabled={
                    lobDetailsForm &&
                    lobDetailsForm.pristine &&
                    lobPropertiesForm &&
                    lobPropertiesForm.pristine
                  }
                  handleReset={(e) => {
                    e.stopPropagation();
                    handleDetailsFormReset();
                  }}
                  submitDisabled={
                    !canEdit ||
                    (lobDetailsForm &&
                      (lobDetailsForm.pristine || !lobDetailsForm.valid) &&
                      lobPropertiesForm &&
                      lobPropertiesForm.pristine)
                  }
                  handleSubmit={(e) => {
                    e.stopPropagation();
                    e.preventDefault();

                    if (!lobDetailsForm.valid) return;

                    if (process.env.REACT_APP_ENV !== 'production')
                      handleSubmit(e);
                    else handlePreSubmit(e, 'lobProperties');
                  }}
                />
              ) : null}
            </AccordionSummary>
            <AccordionDetails style={{ backgroundColor: 'white' }}>
              <LOBDetailsForm
                ref={detailsFormRef}
                submitError={detailsFormError}
                initialValues={formValue}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expanded === 'panel2'}
            onChange={togglePanel('panel2')}>
            <AccordionSummary
              aria-controls="panel2bh-content"
              id="panel2bh-header">
              <Typography
                variant="formHeader"
                sx={{ flex: 2, maxWidth: '33%', flexShrink: 1 }}>
                Merchants
              </Typography>

              {expanded === 'panel2' ? null : (
                <Typography
                  sx={{ flex: 2, color: 'text.secondary', flexShrink: 1 }}>
                  Configure channel hashes
                </Typography>
              )}
              <ProdConfirmationDialog
                openDialog={openDialog2}
                yesHandler={handleChannelsSubmit}
              />
              {/* Header: Actions when expanded */}
              {expanded === 'panel2' ? (
                <FormSaveAndSubmit
                  id={id}
                  resetDisabled={lobChannelsForm && lobChannelsForm.pristine}
                  handleReset={(e) => {
                    e.stopPropagation();
                    handleChannelsReset();
                  }}
                  submitDisabled={
                    !canEdit ||
                    (lobChannelsForm &&
                      (lobChannelsForm.pristine || !lobChannelsForm.valid))
                  }
                  handleSubmit={(e) => {
                    e.stopPropagation();
                    e.preventDefault();

                    if (!lobChannelsForm.valid) return;

                    if (process.env.REACT_APP_ENV !== 'production')
                      handleChannelsSubmit(e);
                    else handlePreSubmit(e, 'merchants');
                  }}
                />
              ) : null}
            </AccordionSummary>
            <AccordionDetails>
              <LOBChannelsForm
                ref={channelsFormRef}
                initialValues={formValue}
                submitError={channelsFormError}
              />
            </AccordionDetails>
          </Accordion>

          {id && (
            <Accordion
              expanded={expanded === 'panel3'}
              onChange={togglePanel('panel3')}>
              <AccordionSummary
                aria-controls="panel2bh-content"
                id="panel2bh-header">
                <Typography
                  variant="formHeader"
                  sx={{ flex: 2, maxWidth: '33%', flexShrink: 1 }}>
                  Payment Methods
                </Typography>

                {expanded === 'panel3' ? null : (
                  <Typography
                    sx={{ flex: 2, color: 'text.secondary', flexShrink: 1 }}>
                    Configure tab order and methods per platform
                  </Typography>
                )}

                {/* Header: Actions when expanded */}
                {expanded === 'panel3' ? (
                  <FormSaveAndSubmit
                    id={id}
                    resetDisabled={!tabOrderChanged}
                    handleReset={(e) => {
                      e.stopPropagation();

                      handleTabOrderReset();
                    }}
                    submitDisabled={!canEdit || !tabOrderChanged}
                    handleSubmit={(e) => {
                      e.stopPropagation();

                      handleTabOrderSubmit();
                    }}
                  />
                ) : null}
              </AccordionSummary>
              <AccordionDetails>
                <LOBPaymentMethods
                  resetTabOrder={!tabOrderChanged}
                  onTabOrderChange={handleTabOrderChange}
                  submitError={tabsFormError}
                  submitSuccess={tabsFormSuccess}
                />
              </AccordionDetails>
            </Accordion>
          )}

          {/* Contextual Messages */}
          {id && (
            <Accordion
              expanded={expanded === 'panel4'}
              onChange={togglePanel('panel4')}>
              <AccordionSummary
                aria-controls="contextual-messages-content"
                id="contextual-messages-header">
                <Typography
                  variant="formHeader"
                  sx={{ flex: 2, maxWidth: '33%', flexShrink: 1 }}>
                  Contextual Messages
                </Typography>

                {expanded === 'panel4' ? null : (
                  <Typography
                    sx={{ flex: 2, color: 'text.secondary', flexShrink: 1 }}>
                    Configure contextual messages per platform
                  </Typography>
                )}
              </AccordionSummary>
              <AccordionDetails>
                <LOBContextualMessages />
              </AccordionDetails>
            </Accordion>
          )}

          {id && (
            <Accordion
              expanded={expanded === 'panel5'}
              onChange={togglePanel('panel5')}>
              <AccordionSummary
                aria-controls="processing-fees-content"
                id="processing-fees-header">
                <Typography
                  variant="formHeader"
                  sx={{ flex: 2, maxWidth: '33%', flexShrink: 1 }}>
                  Processing Fees
                </Typography>

                {expanded === 'panel5' ? null : (
                  <Typography
                    sx={{ flex: 2, color: 'text.secondary', flexShrink: 1 }}>
                    Processing Fees per LOB
                  </Typography>
                )}
              </AccordionSummary>
              <AccordionDetails>
                <ProcessingFees initialValues={formValue} />
              </AccordionDetails>
            </Accordion>
          )}
        </div>
      )}
    </ApplicationPageTemplate>
  );
}
